import React, { CSSProperties, PropsWithChildren, ReactNode, Ref } from 'react';
import { PaddingType } from '../panel';
import { Property } from 'csstype';
import { GridTemplatePanel } from './components/GridTemplatePanel';
import { FlexTemplatePanel } from './components/FlexTemplatePanel';
import { ResponsiveTemplatePanel } from './components/ResponsiveTemplatePanel';

/**
 * {@docCategory TemplatePanel}
 */
export type TLength = string | number;

/**
 * {@docCategory TemplatePanel}
 */
export type BreakpointWidth = 'x-small' | 'small' | 'medium' | 'large' | 'x-large';

/**
 * {@docCategory TemplatePanel}
 */
export type FlexItemDefinition = {
   order?: number;
   flex?: number | string;
   alignSelf?: Property.AlignSelf,
   justifySelf?: Property.JustifySelf,
   width?: Property.Width<TLength>;
   height?: Property.Height<TLength>;
   minWidth?: Property.MinWidth<TLength>;
   maxWidth?: Property.MaxWidth<TLength>;
   minHeight?: Property.MinHeight<TLength>;
   maxHeight?: Property.MaxHeight<TLength>;
} & FlexPanelDefinition;

/**
 * {@docCategory TemplatePanel}
 */
export type FlexItemMap = {
   [areaName: string]: FlexItemDefinition;
};

/**
 * {@docCategory TemplatePanel}
 */
export type FlexPanelDefinition = {
   flexDirection?: Property.FlexDirection;
   flexWrap?: Property.FlexWrap;
   justifyContent?: Property.JustifyContent;
   alignItems?: Property.AlignItems;
   alignContent?: Property.AlignContent;
   flexItems?: FlexItemMap;
}

/**
 * {@docCategory TemplatePanel}
 */
export type FlexTemplateDefinition = {
   autoOverflowAreas?: string[] | boolean;
   rows: FlexItemDefinition[]
}

/**
 * {@docCategory TemplatePanel}
 */
export type GridTemplateDefinition = {
   columns: string[];
   rows: string[];
   areas: string[];
   justifyItems?: Property.JustifyItems;
   justifyContent?: Property.JustifyContent;
   alignItems?: Property.AlignItems;
   alignContent?: Property.AlignContent;
   autoRows?: string;
   autoColumns?: string;
   autoFlow?: string;
   autoOverflowAreas?: string[] | boolean;
}

/**
 * {@docCategory TemplatePanel}
 */
export type TemplateDefinition = GridTemplateDefinition | FlexTemplateDefinition;

/**
 * {@docCategory TemplatePanel}
 */
export type ResponsiveTemplateDefinition = {
   [breakpointName in BreakpointWidth]?: TemplateDefinition | BreakpointWidth
}

/**
 * {@docCategory TemplatePanel}
 */
export type GapProperty = boolean | 'small' | 'medium' | 'large';

/**
 * {@docCategory TemplatePanel}
 */
export interface TemplatePanelProps {
   /**
    * The template to use.
    */
   template: TemplateDefinition | ResponsiveTemplateDefinition;

   /**
    * The gap between the columns and rows.
    *
    * @defaultvalue medium
    */
   gap?: GapProperty;

   /**
    * The gap between the columns.
    *
    * @defaultvalue medium
    */
   hgap?: GapProperty;

   /**
    * The gap between the rows.
    *
    * @defaultvalue medium
    */
   vgap?: GapProperty;

   /**
    * Determines if the component has a border around it.
    *
    * @defaultvalue false
    */
   border?: boolean;

   /**
    * Determines if the panel has padding around the content.
    *
    * @defaultvalue false
    */
   padding?: boolean | 'small' | 'medium' | 'large';

   /**
    * Determines if the panel has left padding
    *
    * @defaultvalue false
    */
   paddingLeft?: PaddingType;

   /**
    * Determines if the panel has right padding
    *
    * @defaultvalue false
    */
   paddingRight?: PaddingType;

   /**
    * Determines if the panel has top padding
    *
    * @defaultvalue false
    */
   paddingTop?: PaddingType;

   /**
    * Determines if the panel has bottom padding
    *
    * @defaultvalue false
    */
   paddingBottom?: PaddingType;

   /**
    * Used to enable the built in overflow configuration for the content area.
    * @defaultvalue false
    */
   scroll?: boolean;

   /**
    * Determines if the panel will fill it's parent containers width and height.
    *
    * @defaultvalue false
    */
   fill?: boolean;

   /**
    * Determines if the panel will fill it's parent containers width.
    *
    * @defaultvalue false
    */
   horizontalFill?: boolean;

   /**
    * Determines if the panel will fill it's parent containers height.
    *
    * @defaultvalue false
    */
   verticalFill?: boolean;

   /**
    * The width of the panel.
    *
    * @defaultvalue 100%
    */
   width?: string | number;

   /**
    * The height of the panel.
    *
    * @defaultvalue 100%
    */
   height?: string | number;

   /**
    * The minimum width of the panel.
    *
    * @defaultvalue 0
    */
   minWidth?: string | number;

   /**
    * The mimimum height of the panel.
    *
    * @defaultvalue 0
    */
   minHeight?: string | number;

   /**
    * The maximum width of the panel.
    *
    * @defaultvalue undefined
    */
   maxWidth?: string | number;

   /**
    * The maximum height of the panel.
    *
    * @defaultvalue undefined
    */
   maxHeight?: string | number;

   /**
    * Sets a className on the outer panel.
    */
   className?: string;

   /**
    * Sets styles on the outer panel.
    */
   style?: CSSProperties;

   /**
    * Sets the reference property
    */
   ref?: Ref<HTMLDivElement>;

   /**
    * Sets the id of the element in the DOM.
    */
   id?: string;

   /**
    * The children TemplateAreas
    */
   children?: ReactNode;
}

export const TemplatePanel = React.forwardRef<HTMLDivElement, TemplatePanelProps>(
   (props: TemplatePanelProps, ref: Ref<HTMLDivElement>) => {

      const template = props.template;

      if (isTemplateDefinition(template)) {

         // Not responsive (no breakpoints)

         if (isGridTemplateDefinition(template)) {
            return (<GridTemplatePanel
               {...props}
               template={template}
               ref={ref}
            >
               {props.children}
            </GridTemplatePanel>);
         }
         else {
            return (<FlexTemplatePanel
               {...props}
               template={template}
               ref={ref}
            >
               {props.children}
            </FlexTemplatePanel>);
         }
      }

      // Responsive (has breakpoints)

      return (<ResponsiveTemplatePanel
         {...props}
         template={template}
      >
         {props.children}
      </ResponsiveTemplatePanel>);
   }
);

function isTemplateDefinition(obj: any): obj is TemplateDefinition {
   return obj && obj.rows !== undefined;
}

function isGridTemplateDefinition(obj: any): obj is GridTemplateDefinition {
   return obj && obj.columns !== undefined;
}
